import type { IntrinsicSystemProps } from '@hover/blueprint';
import { forwardRef, system, useLatestCallback } from '@hover/blueprint';
import type { MotionValue } from 'framer-motion';
import { animate, motion, motionValue } from 'framer-motion';
import type { RefCallback } from 'react';
import React, {
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import { useRefMap } from 'src/shared/hooks/useRefMap';

import { placeSegmentHandle } from './helpers';
import { useSegmentMaterials } from './hooks';
import type {
  HandlePlacementProps,
  Placement,
  Segment,
  SegmentHandleInstancePlacementHandler,
  SegmentHandleMaterialPlacementHandler,
  SegmentationProps,
  SelectedSegments,
} from './types';

interface LargestAreaResult {
  largestArea: number;
  largestElement: SVGPathElement | null;
}

interface HandlePlacement {
  placement: Placement | null;
  area: number;
}

/**
 * Imperative handle ref for accessing functions that provide information about
 * the "dot" region handle placement for a given segment
 */
interface HandlePlacementFunctions {
  getPlacement: () => Placement | null;
  getArea: () => number | null;
}

interface SegmentHighlightProps
  extends IntrinsicSystemProps<'g'>,
    HandlePlacementProps {
  paths: string[];
  masks?: string[];
  opacityMotion: MotionValue<number>;
  /**
   * Imperative handle ref for accessing functions that provide information
   * about the "dot" region handle placement for the segment this higlight
   * represents. This is necessary to expose as an imperative handle in order to
   * determine handle placement for entire material classes which are composed
   * of multiple instance segments.
   */
  handlePlacementFunctionsRef?: RefCallback<HandlePlacementFunctions>;
}

const SegmentHighlight = forwardRef<SegmentHighlightProps, 'g'>(
  (
    {
      debug = false,
      masks,
      handlePlacementFunctionsRef,
      opacityMotion,
      paths,
      weighted = true,
      ...props
    },
    ref,
  ) => {
    const maskId = useId();

    const [handlePlacement, setHandlePlacement] = useState<Placement | null>(
      null,
    );

    const { getRef: getPathRef, getElements: getPathElements } = useRefMap<
      string,
      SVGPathElement
    >();

    const { getRef: getMaskRef, getElements: getMaskElements } = useRefMap<
      string,
      SVGPathElement
    >();

    const findLargestAreaElement = (): LargestAreaResult => {
      const elements = getPathElements();
      return Object.values(elements).reduce<LargestAreaResult>(
        (acc, element) => {
          if (element) {
            const bbox = element.getBBox();
            const area = bbox.width * bbox.height;
            if (area > acc.largestArea) {
              return { largestArea: area, largestElement: element };
            }
          }
          return acc;
        },
        { largestArea: 0, largestElement: null },
      );
    };

    useImperativeHandle(handlePlacementFunctionsRef, () => ({
      getPlacement: () => {
        const { largestElement } = findLargestAreaElement();
        if (!largestElement) return null;

        const placement = placeSegmentHandle({
          path: largestElement,
          masks: getMaskElements(),
          weighted,
        });

        setHandlePlacement(placement);
        return placement;
      },
      getArea: () => {
        const { largestArea } = findLargestAreaElement();
        return largestArea || null;
      },
    }));

    if (paths.length === 0) return null;

    const debugPlacement = handlePlacement && debug;

    return (
      <system.g {...props} ref={ref}>
        {paths.map(d => (
          <motion.path
            cursor="pointer"
            d={d}
            fill="#fff"
            key={d}
            mask={`url(#${maskId})`}
            ref={getPathRef(d)}
            style={{ opacity: opacityMotion }}
          />
        ))}
        {masks && (
          <system.mask id={maskId}>
            <rect fill="#fff" height="100%" width="100%" x={0} y={0} />
            {masks.map(mask => (
              <path d={mask} fill="#000" key={mask} ref={getMaskRef(mask)} />
            ))}
          </system.mask>
        )}
        {debugPlacement &&
          handlePlacement.search.map(({ x, y, weight }) => (
            <circle
              cx={x}
              cy={y}
              fill={`rgba(0, 255, 0, ${5 * weight})`}
              key={`${x}-${y}`}
              r={2}
            />
          ))}
        {debugPlacement && (
          <system.circle
            cx={handlePlacement.x}
            cy={handlePlacement.y}
            fill="red"
            r={5}
          />
        )}
      </system.g>
    );
  },
);

export type HighlightMode = 'current' | 'difference';

export interface SegmentationHighlightsProps
  extends SegmentationProps,
    HandlePlacementProps,
    IntrinsicSystemProps<'svg'> {
  /**
   * Blur radius to apply to segment highlight paths
   */
  blur?: number;
  /**
   * Blur radius and inset in percentage for feathering the edge of the
   * segmentation highlight SVG to prevent highlights from extending past the
   * edge of the image in cases where it doesn't align perfectly.
   *
   * Set to 0 to disable feathering altogether.
   *
   * @defaultValue 1
   */
  feather?: number;
  highlightDuration?: number;
  /**
   * Determines how highlights appear in response to segment selection changes.
   *
   * - When set to `'current'`, the highlights representing the current
   *   selection will be faded in and out
   * - When set to `'difference'`, the highlights that were added and/or removed
   *   will be faded in and out
   */
  highlightMode?: HighlightMode;
  onPlaceInstanceHandle?: SegmentHandleInstancePlacementHandler;
  onPlaceMaterialHandle?: SegmentHandleMaterialPlacementHandler;
}

const createSegmentMotionValues = (segments: Segment[]) =>
  Object.fromEntries(segments.map(({ id }) => [id, motionValue(0)]));

/**
 * Compare two arrays where equality means that the two arrays
 * have the same elements regardless of order or duplicates.
 */
const areArraysEqual = (a: unknown[], b: unknown[]) => {
  const setA = new Set(a);
  const setB = new Set(b);

  if (setA.size !== setB.size) return false;

  return b.every(item => setA.has(item));
};

export const SegmentationHighlights = forwardRef<
  SegmentationHighlightsProps,
  'svg'
>(
  (
    {
      blur = 2,
      data: { dimensions, segments },
      debug = false,
      feather = 1,
      handlePlacementMode = 'material',
      highlightDuration = 1.25,
      highlightMode = 'current',
      onPlaceInstanceHandle: onPlaceInstanceHandleProp = () => {},
      onPlaceMaterialHandle: onPlaceMaterialHandleProp = () => {},
      selectedSegments = [],
      weighted,
      ...props
    },
    ref,
  ) => {
    const overlayId = useId();
    const segmentFilterId = `segment-filter-${overlayId}`;
    const featherFilterId = `feather-filter-${overlayId}`;
    const featherMaskId = `feather-mask-${overlayId}`;

    const previousSelectedRef = useRef<SelectedSegments>(selectedSegments);

    const materials = useSegmentMaterials(segments);

    const {
      getElement: getHandlePlacementFunctions,
      getRef: getHandlePlacementFunctionsRef,
    } = useRefMap<string, HandlePlacementFunctions>();

    const onPlaceInstanceHandle = useLatestCallback(onPlaceInstanceHandleProp);
    const onPlaceMaterialHandle = useLatestCallback(onPlaceMaterialHandleProp);

    // Dispatch effect events for handle placement by instance or segment
    // once paths have been measured and placements calculated after first
    // render and when segment data changes based on `handlePlacementMode`
    useEffect(() => {
      if (handlePlacementMode === 'instance') {
        segments.forEach(segment => {
          const { id } = segment;
          const placement = getHandlePlacementFunctions(id)?.getPlacement();

          if (placement) onPlaceInstanceHandle(segment, placement);
        });

        return;
      }

      materials.forEach(material => {
        const { placement: materialPlacement } = segments
          .filter(segment => segment.material === material)
          .reduce(
            (previous, { id }) => {
              const functions = getHandlePlacementFunctions(id);

              if (!functions) return previous;

              const { getPlacement, getArea } = functions;
              const placement = getPlacement();
              const area = getArea();

              return placement && area && area > previous.area
                ? { placement, area }
                : previous;
            },
            { placement: null, area: 0 } as HandlePlacement,
          );

        if (materialPlacement) {
          onPlaceMaterialHandle(material, materialPlacement);
        }
      });
    }, [
      getHandlePlacementFunctions,
      handlePlacementMode,
      materials,
      onPlaceInstanceHandle,
      onPlaceMaterialHandle,
      segments,
    ]);

    // Create a mapping of motion values for each segment to animate the opacity
    // of the segment highlight for the highlight animation effect
    const motionValuesRef = useRef(createSegmentMotionValues(segments));
    const { current: motionValues } = motionValuesRef;

    // Ensure the mapping of motion values used to drive the highlight animation
    // effect is always in sync with the available `segments` in `data`
    if (
      !areArraysEqual(
        Object.keys(motionValues),
        segments.map(({ id }) => id),
      )
    ) {
      motionValuesRef.current = createSegmentMotionValues(segments);
    }

    // Respond to changes in selected segments by animating the highlights
    // representing the selection change depending on the `highlightMode`
    useEffect(() => {
      if (!previousSelectedRef.current) return;

      const { current: previousSelected } = previousSelectedRef;

      const previous = new Set(previousSelected);
      const current = new Set(selectedSegments);

      previousSelectedRef.current = selectedSegments;

      const added = [...current].filter(item => !previous.has(item));
      const removed = [...previous].filter(item => !current.has(item));

      if (added.length === 0 && removed.length === 0) return;

      const highlights =
        highlightMode === 'current' ? selectedSegments : [...added, ...removed];

      highlights.forEach(id => {
        const opacityMotion = motionValues[id];

        return animate(opacityMotion, [opacityMotion.get(), 1, 0], {
          duration: highlightDuration,
          ease: 'easeOut',
        });
      });
    }, [highlightDuration, highlightMode, motionValues, selectedSegments]);

    const { width, height } = dimensions;
    const inset =
      feather > 0 ? (width > height ? width : height) / (100 / feather) : false;

    return (
      <system.svg
        bottom={0}
        left={0}
        mixBlendMode={debug ? undefined : 'soft-light'}
        position="absolute"
        right={0}
        top={0}
        viewBox={`0 0 ${width} ${height}`}
        {...props}
        ref={ref}
      >
        <g
          filter={debug ? undefined : `url(#${segmentFilterId})`}
          mask={inset ? `url(#${featherMaskId})` : undefined}
        >
          {segments.map(({ id, masks, material, paths, surface }) => (
            <SegmentHighlight
              // These data attributes are currently
              // only included for debugging purposes
              data-instance-id={id}
              data-material={material}
              data-surface={surface}
              debug={debug}
              handlePlacementFunctionsRef={getHandlePlacementFunctionsRef(id)}
              key={id}
              masks={masks}
              opacityMotion={motionValues[id]}
              paths={paths}
              weighted={weighted}
            />
          ))}
        </g>
        {inset && (
          <mask id={featherMaskId}>
            <rect
              fill="#fff"
              filter={`url(#${featherFilterId})`}
              height={height - inset * 2}
              rx={inset}
              ry={inset}
              width={width - inset * 2}
              x={inset}
              y={inset}
            />
          </mask>
        )}
        <defs>
          <filter
            colorInterpolationFilters="sRGB"
            filterUnits="userSpaceOnUse"
            id={segmentFilterId}
          >
            <feGaussianBlur stdDeviation={blur} />
          </filter>
          {inset && (
            <filter
              colorInterpolationFilters="sRGB"
              filterUnits="userSpaceOnUse"
              id={featherFilterId}
            >
              <feGaussianBlur stdDeviation={inset / 1.5} />
            </filter>
          )}
        </defs>
      </system.svg>
    );
  },
);
